import { createTheme } from '@mui/material/styles';

import { vidro, brilhoDeTopo, reflexoDoPonteiro, CURVAS, TEMPOS } from './vidro';

/**
 * Tema do ConsultorIA.
 *
 * Princípio: é uma ferramenta de trabalho para empresário e consultor, não uma
 * landing page. A interface recua para que o número apareça.
 *
 * - Cor tem significado. Verde, vermelho e âmbar dizem se o resultado é bom,
 *   ruim ou merece atenção — nunca são enfeite. Todo o resto é neutro.
 * - Um acento só: azul-petróleo, para navegação e ação primária.
 * - Elevação declarada uma vez, agora em vidro: a superfície é translúcida,
 *   com filete claro na quina de cima e escurecimento na de baixo. A sombra
 *   projetada existe — vidro flutua sobre o fundo que ele refrata —, mas é uma
 *   só, longa e muito difusa; a regra antiga ("sem sombra por baixo da borda")
 *   descrevia o produto claro e chapado que este redesenho substituiu.
 * - Numerais tabulares: dinheiro em coluna precisa alinhar dígito com dígito.
 */

// Neutros levemente frios — cinza puro achata, cinza azulado dá profundidade
const neutro = {
  900: '#111827',
  700: '#374151',
  600: '#4b5563',
  500: '#6b7280',
  300: '#d1d5db',
  200: '#e5e7eb',
  100: '#f0f1f4',
  50: '#f6f7f9'
};

// Acento único: azul azure, a MESMA família dos dois temas.
//
// O claro usava #2c4a6e, um azul-petróleo de outra paleta: o tema claro tinha
// literalmente outra marca — botão, link e pílula de navegação saíam num azul
// que não existe no escuro. Aqui ele é o mesmo matiz do #6AA6FF, escurecido até
// passar em texto: 5,03:1 sobre o fundo claro do produto, medido. O #6AA6FF
// direto reprovaria (2,18:1) — sobre branco ele é cor de fundo, nunca de texto.
const acento = {
  main: '#2563c7',
  light: '#4d84dd',
  dark: '#1a4a9c',
  contrastText: '#ffffff'
};

// Semânticas: escuras o bastante para texto sobre branco (>= 4.5:1)
const semantica = {
  positivo: { main: '#116b45', light: '#2f8f66', dark: '#0b5334', contrastText: '#ffffff' },
  negativo: { main: '#b42318', light: '#d14b3f', dark: '#8f1c12', contrastText: '#ffffff' },
  atencao: { main: '#b54708', light: '#dc6803', dark: '#8a3606', contrastText: '#ffffff' }
};

/**
 * Cores de série para os gráficos.
 *
 * Regra: verde e vermelho só entram quando a série significa resultado bom ou
 * ruim. Duas linhas de custo são duas categorias, não um julgamento — por isso
 * usam o acento e um neutro com matiz, e não verde contra roxo.
 *
 * ESTA É A PALETA CLARA, e ela é a única que existia. Como era constante de
 * módulo, o tema escuro herdava cor pensada para fundo branco: a legenda
 * "Faturamento" saía em #2c4a6e sobre #141d2a, medidos 1,87:1 — texto que
 * simplesmente não se lê. "Resultado com CMV" ficava em 2,59:1 e "Ponto de
 * equilíbrio" em 3,12:1. Quem usa o produto à noite via um gráfico com legenda
 * invisível e nenhuma forma de saber qual linha era qual.
 */
export const CORES_GRAFICO = {
  primaria: acento.main,
  secundaria: '#8fa3b8',
  terciaria: '#6b7f96',
  positiva: semantica.positivo.main,
  negativa: semantica.negativo.main,
  atencao: semantica.atencao.main,
  grade: '#e5e7eb',
  eixo: '#6b7280'
};

/**
 * A mesma paleta, reescrita para o fundo escuro.
 *
 * Todos os matizes clareados até passarem de 4,5:1 contra a superfície escura
 * (#141d2a), que é o mínimo para TEXTO — a legenda é texto. Os pares vizinhos
 * mantêm ΔE deutan >= 9, a mesma validação de daltonismo que a barra de
 * composição do Balanço documenta: em série de linha, duas cores que colapsam
 * para o mesmo cinza tornam o gráfico ilegível para 8% dos homens.
 */
const CORES_GRAFICO_ESCURO = {
  primaria: '#6aa6ff',
  secundaria: '#9db4cc',
  terciaria: '#7f9bb8',
  positiva: '#3ecf8e',
  negativa: '#ff8a7d',
  atencao: '#e9a33c',
  // A grade some quase por completo: sobre vidro, linha de grade forte compete
  // com a borda da própria superfície e o gráfico vira gaiola.
  grade: 'rgba(255, 255, 255, 0.07)',
  eixo: '#8b97a8'
};

/**
 * A paleta de gráfico do tema em uso.
 *
 * Toda tela que desenha série chama isto com `theme.palette.mode === 'dark'`,
 * em vez de importar a constante. É a mesma correção que a tela de Balanço já
 * fazia sozinha, agora em um lugar só para as três que faltavam.
 */
export const coresDoGrafico = (escuro) => (escuro ? CORES_GRAFICO_ESCURO : CORES_GRAFICO);

// A pilha do sistema fica como CAUDA de segurança — se o woff2 não carregar, o
// texto aparece de imediato numa fonte próxima, e não numa serifada de último
// recurso. O primeiro nome é o arquivo que o produto serve.
const fontStack = [
  '"Inter Variable"',
  'Inter',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif'
].join(',');

const tipografia = {
  fontFamily: fontStack,
  // Dígitos de largura fixa: sem isso uma coluna de valores dança a cada mês
  // `tnum` alinha dígito com dígito; `cv05` é a variante do Inter que abre o
  // "l" com cauda, para ele não se confundir com "1" e "I" numa coluna de
  // números. Antes deste commit a fonte não era carregada e `cv05` não fazia
  // nada — pedido de variante a uma família ausente.
  fontFeatureSettings: '"tnum" 1, "cv05" 1',
  // A escala tinha degraus de menos: h4 e h5 quase se tocavam, e o título de
  // uma tela pesava o mesmo que o de um bloco dentro dela. Quando todo texto
  // tem o mesmo tamanho, o olho não sabe por onde entrar — e a tela lê como
  // formulário, não como leitura. Razão de ~1,2 entre passos, e peso 700 na
  // manchete: hierarquia se faz com dois eixos, tamanho e peso.
  h1: { fontSize: '2.125rem', fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.2 },
  h2: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.25 },
  h3: { fontSize: '1.625rem', fontWeight: 650, letterSpacing: '-0.02em', lineHeight: 1.25 },
  h4: { fontSize: '1.25rem', fontWeight: 650, letterSpacing: '-0.012em', lineHeight: 1.35 },
  h5: { fontSize: '1.0625rem', fontWeight: 600, letterSpacing: '-0.008em', lineHeight: 1.4 },
  h6: { fontSize: '0.9375rem', fontWeight: 600, letterSpacing: '-0.004em', lineHeight: 1.45 },
  subtitle1: { fontSize: '0.9375rem', fontWeight: 500 },
  subtitle2: { fontSize: '0.8125rem', fontWeight: 600, letterSpacing: '0.01em' },
  body1: { fontSize: '0.875rem', lineHeight: 1.6 },
  body2: { fontSize: '0.8125rem', lineHeight: 1.6 },
  caption: { fontSize: '0.75rem', lineHeight: 1.5, letterSpacing: '0.01em' },
  button: { textTransform: 'none', fontWeight: 500, letterSpacing: 0 }
};

// A escada de sombra do MUI, usada por menu, diálogo e ancoradouro. O CARTÃO
// NÃO A USA: ele monta a própria profundidade em `theme/vidro.js` — filete na
// quina de cima, escurecimento na de baixo e uma sombra longa e difusa. Este
// comentário dizia "card usa borda", que descrevia o produto claro e chapado
// que o redesenho de 2026-08-20 substituiu.
const sombras = [
  'none',
  '0 1px 2px rgba(17, 24, 39, 0.04)',
  '0 1px 3px rgba(17, 24, 39, 0.06)',
  '0 2px 6px rgba(17, 24, 39, 0.07)',
  '0 6px 16px rgba(17, 24, 39, 0.09)',
  '0 12px 28px rgba(17, 24, 39, 0.12)',
  '0 20px 40px rgba(17, 24, 39, 0.16)',
  ...Array(18).fill('0 20px 40px rgba(17, 24, 39, 0.16)')
];

const componentes = (modo) => {
  const escuro = modo === 'dark';
  // Linha de divisão é ALFA, e não um cinza fixo: sobre vidro translúcido, um
  // hexadecimal opaco vira faixa cinza visível e denuncia a superfície.
  const linha = escuro ? 'rgba(255, 255, 255, 0.09)' : 'rgba(15, 23, 34, 0.09)';
  const trilho = escuro ? 'rgba(255, 255, 255, 0.04)' : neutro[100];
  const polegar = escuro ? 'rgba(255, 255, 255, 0.16)' : neutro[300];
  const foco = escuro ? '#6aa6ff' : acento.main;

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: `${polegar} transparent`,
          '&::-webkit-scrollbar': { width: '10px', height: '10px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: polegar,
            borderRadius: '99px',
            border: '3px solid transparent',
            backgroundClip: 'content-box'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: escuro ? 'rgba(255,255,255,0.26)' : neutro[500],
            backgroundClip: 'content-box'
          }
        },
        // A seleção de texto usa o acento do produto. Detalhe que ninguém pede e
        // todo produto caro tem.
        '::selection': {
          background: escuro ? 'rgba(106, 166, 255, 0.32)' : 'rgba(44, 74, 110, 0.18)'
        },
        // Valores monetários herdam alinhamento de coluna em toda a aplicação
        'table, .valor-numerico': { fontVariantNumeric: 'tabular-nums' }
      }
    },
    // BOTÃO DE ÍCONE TEM DE TER 44px, QUE É O DEDO.
    //
    // O padrão do MUI é 40x40 (24 de ícone + 8 de recheio de cada lado), e é
    // com ele que estavam TODOS os controles do cabeçalho: abrir o menu, trocar
    // o tema, exportar o PDF, mais ações. Quatro pixels a menos que o mínimo,
    // no aparelho em que o empresário usa o produto, e justamente nos botões
    // que ficam colados na borda da tela — onde errar o alvo é mais fácil.
    // Como o botão é redondo e mora numa barra de 64px, crescer 4px não
    // reposiciona nada.
    MuiIconButton: {
      styleOverrides: {
        sizeMedium: { width: 44, height: 44 },
        // O estado do botão de ícone é o estado do vidro: a superfície acende.
        // Nada de `scale` — alvo que muda de tamanho debaixo do ponteiro é o
        // gesto que este produto passou a reservar para as duas peças que
        // realmente se movem.
        root: {
          transition: `background-color ${TEMPOS.estado}ms ${CURVAS.estado}, color ${TEMPOS.estado}ms ${CURVAS.estado}`,
          '&:hover': {
            backgroundColor: escuro ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,34,0.05)'
          }
        }
      }
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontSize: '0.875rem',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          // NO DEDO, TODO BOTÃO TEM 44px DE ALTURA.
          //
          // Medido no aparelho de 390px, tela por tela: "Salvar", "Sair",
          // "Ver de onde vem", "Ver todos", "Completar os dados", "Informar o
          // balanço", "Baixar planilha" e "Recolher tudo" mediam 41px — todos os
          // botões de texto do produto, e não só os do cabeçalho como estava
          // registrado. 41 contra os 44 que Apple e Google pedem: três pixels que
          // ninguém vê e que fazem o polegar errar o alvo no celular, que é o
          // aparelho em que este produto é usado.
          //
          // A regra vive em `pointer: coarse` porque o problema é do dedo. No
          // desktop, onde o ponteiro tem a precisão de um pixel, 41px é a
          // densidade que as telas foram desenhadas para ter, e engordar tudo
          // afastaria as linhas sem ganho nenhum.
          '@media (pointer: coarse)': { minHeight: 44 }
        },
        // A ação primária é a única peça que carrega cor cheia — e ganha o mesmo
        // bisel do vidro: filete claro no topo, sombra curta embaixo. É o que
        // separa um botão de um retângulo pintado.
        contained: {
          boxShadow: escuro
            ? 'inset 0 1px 0 rgba(255,255,255,0.22), 0 1px 2px rgba(0,0,0,0.4), 0 8px 20px -10px rgba(0,0,0,0.8)'
            : 'inset 0 1px 0 rgba(255,255,255,0.28), 0 1px 2px rgba(15,23,34,0.18)',
          transition: `transform ${TEMPOS.toque}ms ${CURVAS.estado}, filter ${TEMPOS.estado}ms ${CURVAS.estado}`,
          '&:hover': {
            boxShadow: escuro
              ? 'inset 0 1px 0 rgba(255,255,255,0.26), 0 1px 2px rgba(0,0,0,0.4), 0 10px 26px -10px rgba(0,0,0,0.85)'
              : 'inset 0 1px 0 rgba(255,255,255,0.32), 0 2px 4px rgba(15,23,34,0.2)',
            filter: 'brightness(1.06)'
          },
          '&:active': { transform: 'scale(0.985)' },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:active': { transform: 'none' } }
        },
        outlined: {
          borderColor: linha,
          backdropFilter: 'blur(8px)',
          '&:hover': { borderColor: escuro ? 'rgba(255,255,255,0.2)' : linha }
        }
      }
    },
    // A CAIXA DE SELEÇÃO OBEDECE À MESMA REGRA DE DEDO QUE O BOTÃO.
    //
    // A regra de 44px acima vive em MuiButton, e por isso valia só para botão.
    // Quando o plano de ação ganhou a marcação de feita, o controle novo nasceu
    // fora dela: medido no aparelho de 390px, 38px (ícone de 20 + 9 de recheio
    // dos dois lados) contra os 44 que o resto do produto respeita — e logo no
    // gesto que a Frente 2 existia para entregar.
    //
    // 12 de recheio dá exatamente 20 + 24 = 44. Fica na consulta de ponteiro grosso pelo
    // mesmo motivo do botão: no desktop o ponteiro tem precisão de um pixel, e
    // engordar a caixa só afastaria a lista sem ninguém ganhar nada.
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '@media (pointer: coarse)': { padding: 12 }
        }
      }
    },
    // Todo controle tem os seis estados, e o foco por teclado é visível em
    // todos: sem isso, quem navega sem mouse não sabe onde está. O padrão do MUI
    // é a ondulação, que só aparece no clique — no Tab não sobra sinal nenhum, e
    // a pessoa passa a apertar Enter no escuro.
    //
    // ESTE BLOCO ERA DECLARADO DUAS VEZES no mesmo objeto. Em JavaScript a
    // segunda chave vence, então a primeira versão — a única que usava o token
    // `foco` — era descartada em silêncio, e o anel que sobrevivia trazia dois
    // hexadecimais escritos à mão fora da paleta.
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': {
            outline: `2px solid ${foco}`,
            outlineOffset: 2
          }
        }
      }
    },
    // O polegar do controle deslizante não é um `ButtonBase`, então ficava de
    // fora da regra acima: nas quatro paradas do simulador de cenário o Tab não
    // desenhava nada, e quem navega sem mouse atravessava quatro controles sem
    // ver onde estava. Anel no polegar, mesmo tratamento dos botões.
    MuiSlider: {
      styleOverrides: {
        thumb: {
          '&.Mui-focusVisible, &:focus-visible': {
            outline: `2px solid ${foco}`,
            outlineOffset: 2,
            boxShadow: 'none'
          }
        }
      }
    },
    // O CARTÃO É A SUPERFÍCIE DE VIDRO DO PRODUTO.
    //
    // Todo bloco de leitura das dez telas é um `Card`, então a receita mora
    // aqui e chega às dez de uma vez. Ele deixa passar os campos de cor do
    // fundo, acende um filete no topo (luz vem de cima) e escurece a base — é
    // esse par de filetes que dá espessura à peça. Sem eles, vidro lê como
    // retângulo cinza com opacidade.
    //
    // Sem transform no hover: cartão não é botão, e cartão que sobe promete uma
    // interação que não existe. O que responde ao ponteiro é o REFLEXO, que é o
    // que vidro de verdade faz — muda com o ângulo de quem olha.
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          position: 'relative',
          borderRadius: 16,
          backgroundImage: 'none',
          isolation: 'isolate',
          ...vidro(escuro, 'base'),
          transition: `border-color ${TEMPOS.estado}ms ${CURVAS.estado}`,
          '&::before': { ...brilhoDeTopo(escuro), zIndex: 2 },
          '&::after': { ...reflexoDoPonteiro(escuro), zIndex: 1, borderRadius: 'inherit' },
          '&:hover::after': { opacity: 1 },
          '& > *': { position: 'relative', zIndex: 3 }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 14, backgroundImage: 'none' },
        outlined: { borderColor: linha },
        // Menu e diálogo flutuam SOBRE o conteúdo: vidro mais opaco e mais
        // desfocado, senão o texto de trás atravessa e a lista fica ilegível.
        elevation8: { ...vidro(escuro, 'flutuante') },
        elevation16: { ...vidro(escuro, 'flutuante') },
        elevation24: { ...vidro(escuro, 'flutuante') }
      }
    },
    // Pílula, e não retângulo: o chip é o único elemento arredondado ao máximo
    // da tela, e é por isso que o olho o encontra sem procurar.
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 999,
          height: 24,
          letterSpacing: '0.01em',
          backdropFilter: 'blur(6px)'
        },
        outlined: { borderColor: linha },
        label: { paddingLeft: 10, paddingRight: 10, fontSize: '0.75rem' }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${linha}`, fontVariantNumeric: 'tabular-nums' },
        head: {
          fontWeight: 600,
          fontSize: '0.75rem',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: escuro ? '#8b97a8' : neutro[500],
          backgroundColor: 'transparent'
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10, border: `1px solid ${linha}` },
        standardInfo: { backgroundColor: escuro ? 'rgba(69,107,150,0.12)' : '#f2f5f9' },
        standardSuccess: { backgroundColor: escuro ? 'rgba(47,143,102,0.12)' : '#eef6f2' },
        standardWarning: { backgroundColor: escuro ? 'rgba(220,104,3,0.12)' : '#fdf4ec' },
        standardError: { backgroundColor: escuro ? 'rgba(212,75,63,0.12)' : '#fdf1f0' }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          height: 6,
          backgroundColor: escuro ? 'rgba(255,255,255,0.08)' : neutro[200],
          overflow: 'hidden'
        },
        // A barra preenchida acende: no escuro, cor chapada sobre trilho escuro
        // some. O brilho é curto e colado na barra, nunca um halo.
        bar: {
          borderRadius: 999,
          boxShadow: escuro ? '0 0 12px -2px currentColor' : 'none'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 8 },
        notchedOutline: { borderColor: linha }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: escuro ? 'rgba(22, 26, 33, 0.86)' : 'rgba(17, 24, 39, 0.9)',
          backdropFilter: 'blur(14px) saturate(1.4)',
          border: '1px solid',
          borderColor: escuro ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.14)',
          color: '#ffffff',
          fontSize: '0.75rem',
          fontWeight: 400,
          padding: '7px 11px',
          borderRadius: 10,
          boxShadow: '0 12px 30px -12px rgba(0,0,0,0.7)'
        }
      }
    },
    MuiAppBar: { defaultProps: { elevation: 0 } },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, fontSize: '0.875rem', minHeight: 44 }
      }
    }
  };
};

export const modernTheme = createTheme({
  palette: {
    mode: 'light',
    primary: acento,
    secondary: { main: '#5b6b7f', light: '#7d8b9c', dark: '#42505f', contrastText: '#ffffff' },
    success: semantica.positivo,
    warning: semantica.atencao,
    error: semantica.negativo,
    info: acento,
    // O claro é a contraparte, não o padrão. O fundo sai do branco puro para um
    // cinza-azulado: vidro sobre branco chapado não tem o que refratar, e o
    // cartão desaparece dentro da página.
    background: { default: '#eef1f6', paper: '#ffffff' },
    text: { primary: neutro[900], secondary: neutro[500], disabled: neutro[300] },
    divider: 'rgba(15, 23, 34, 0.09)',
    // SEM ESTE BLOCO A PÍLULA DA NAVEGAÇÃO NASCE CINZA no tema claro: o padrão
    // do MUI para `action.selected` é um preto a 8%, sem matiz nenhum. A peça
    // que carrega a cor da marca em toda tela do escuro perdia a cor ao trocar
    // de tema — e a viagem dela deixava de ser um movimento de cor.
    action: {
      hover: 'rgba(15, 23, 34, 0.045)',
      selected: 'rgba(37, 99, 199, 0.12)'
    },
    grey: neutro
  },
  typography: tipografia,
  shape: { borderRadius: 10 },
  shadows: sombras,
  components: componentes('light')
});

/**
 * O tema escuro é o tema do produto — não a alternativa dele.
 *
 * O fundo é quase preto e levemente frio (#06070A). Ele NÃO é o preto absoluto:
 * sobre #000 o vidro não tem o que refratar e a superfície some. Os campos de
 * cor amplos que ele refrata vivem em `AuroraDeFundo`.
 *
 * Os matizes subiram de luminância em relação à versão anterior porque agora
 * assentam sobre vidro translúcido, e não sobre uma superfície opaca: o mesmo
 * verde que passava 4,5:1 contra #141d2a reprovava contra uma camada que deixa
 * o fundo aparecer.
 */
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#6aa6ff', light: '#93c0ff', dark: '#3d7ad6', contrastText: '#04070D' },
    secondary: { main: '#9aa6b4', light: '#b7c0cb', dark: '#75828f', contrastText: '#04070D' },
    success: { main: '#3ecf8e', light: '#6fe0ac', dark: '#22a36c', contrastText: '#04070D' },
    warning: { main: '#e9a33c', light: '#f2bd6c', dark: '#c07d18', contrastText: '#04070D' },
    error: { main: '#ff8074', light: '#ffa79e', dark: '#d1493c', contrastText: '#04070D' },
    info: { main: '#6aa6ff', light: '#93c0ff', dark: '#3d7ad6', contrastText: '#04070D' },
    // `paper` é o fundo de menu e diálogo, que precisam ser opacos o bastante
    // para o texto de trás não atravessar. O cartão não usa isto: ele é vidro.
    background: { default: '#06070A', paper: '#101319' },
    text: { primary: '#eef2f7', secondary: '#9aa6b4', disabled: '#5a6674' },
    divider: 'rgba(255, 255, 255, 0.09)',
    action: {
      hover: 'rgba(255, 255, 255, 0.05)',
      selected: 'rgba(106, 166, 255, 0.14)',
      disabledOpacity: 0.42
    }
  },
  // Tipografia, forma e sombras são compartilhadas; o que muda é só a paleta.
  // (Antes o objeto do tema claro era espalhado inteiro aqui e sobrescrevia a
  // paleta escura — o modo escuro nunca chegava a aparecer.)
  typography: tipografia,
  shape: { borderRadius: 10 },
  shadows: sombras,
  components: componentes('dark')
});
